import { useCallback, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

/**
 * localStorage does not raise `storage` events in the tab that wrote
 * the value, so the header never learned about a login performed in a
 * modal on the same page. The old workaround was a `useEffect` with no
 * dependency array, which re-read localStorage on literally every
 * render. This custom event replaces it with an explicit signal.
 */
export const AUTH_EVENT = 'bansal-attire:auth-change';

const emitAuthChange = () => {
  window.dispatchEvent(new Event(AUTH_EVENT));
};

/** Credentials the app keeps in localStorage, in one consistent shape. */
export const readAuth = () => ({
  token: localStorage.getItem('token') || '',
  user_id: localStorage.getItem('user_id') || '',
  common_id: localStorage.getItem('common_id') || '',
  user_name: localStorage.getItem('user_name') || '',
});

export const isSignedIn = () => Boolean(localStorage.getItem('token'));

/** Persist a successful login and notify every listening component. */
export const saveAuth = ({ token, user_id, user_name, expiresAt }) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user_id', user_id);
  if (user_name) localStorage.setItem('user_name', user_name);
  if (expiresAt) cookies.set('user_id', user_id, { expires: expiresAt });
  emitAuthChange();
};

/**
 * Update the stored profile details after an edit and notify listeners.
 *
 * `user_name` is only written at login, so editing the profile left the
 * header showing the old name — and, because the stale value lives in
 * localStorage, it survived a reload too, right up until the next
 * sign-in.
 */
export const updateAuthUser = ({ user_name: userName }) => {
  const trimmed = typeof userName === 'string' ? userName.trim() : '';
  if (!trimmed) return;
  localStorage.setItem('user_name', trimmed);
  emitAuthChange();
};

/** Clear every trace of the session, including the admin-path cookie. */
export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_name');
  localStorage.removeItem('common_id');
  cookies.remove('user_id');
  cookies.remove('user_id', { path: '/admin' });
  emitAuthChange();
};

/**
 * Subscribes to the session state. Updates on same-tab logins via the
 * custom event above and on other-tab changes via `storage`.
 */
export const useAuthState = () => {
  const [auth, setAuth] = useState(() => ({
    isLoggedIn: isSignedIn(),
    userName: localStorage.getItem('user_name') || '',
  }));

  const sync = useCallback(() => {
    setAuth({
      isLoggedIn: isSignedIn(),
      userName: localStorage.getItem('user_name') || '',
    });
  }, []);

  useEffect(() => {
    window.addEventListener(AUTH_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(AUTH_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [sync]);

  return { ...auth, refresh: sync };
};

export default readAuth;
